// Stages 1–6 are wired for this test build — 막 I: 린 duel, 브람 duel, 세라's
// 균열 짐승 joint defence · 막 II: 토르's trail read, 니아's orb rite, 칼데르's
// gate rite. The full 15-stage campaign lives in ../../othellos/SCENARIO_OUTLINE.md.
// Every narration/voice string is a `Loc` ({ ko, en }); opponentName/opponentTitle/
// foeName stay Korean (stable keys — see campaign/loc.ts's dName/dTitle).

import type { AiLevel } from "./ai";
import type { Disc } from "./othello";
import type { Loc } from "./campaign/loc.ts";
import {
  ACT1_NAME,
  RIN_PORTRAIT,
  BRAM_PORTRAIT,
  SERA_PORTRAIT,
  SERA_TURN_LINES,
  SERA_COMPANION_PORTRAITS,
  type CompanionTurnLines,
} from "./campaign/act1.ts";
import {
  ACT2_NAME,
  TOR_PORTRAIT,
  NIA_PORTRAIT,
  NIA_TURN_LINES,
  NIA_COMPANION_PORTRAITS,
  CALDER_PORTRAIT,
  ACT2_BG,
} from "./campaign/act2.ts";
import {
  ACT3_NAME,
  ORNA_PORTRAIT,
  MAREK_PORTRAIT,
  IBRA_PORTRAIT,
  LOWEN_PORTRAIT,
  ACT3_BG,
} from "./campaign/act3.ts";
import {
  ACT4_NAME,
  DRABEN_PORTRAIT,
  SILEN_PORTRAIT,
  KAIROS_PORTRAIT,
  ACT4_BG,
} from "./campaign/act4.ts";
import { ACT5_NAME, ARDEN_PORTRAIT, BELA_PORTRAIT, ACT5_BG } from "./campaign/act5.ts";

/** In-character turn-bar lines, spoken by the opponent to the player. */
export interface StageVoice {
  turn: Loc; // player's turn — normal prompt in the opponent's voice
  slow: Loc; // player has taken a while — mild impatience
  behind: Loc; // player is well behind on discs — an ally's encouragement
  thinking: Loc; // the opponent's turn
}

/** When set, the companion voices the player-turn side of the turn bar: one
 *  line per match state (see `CompanionTurnLines`), chosen by priority each
 *  render, no auto-dismiss. The hero illustration follows the current speaker
 *  via `portraits` (name → src). Used by 세라's 공동 방어 (stage 3) and 니아's
 *  미래 고쳐 쓰기 (stage 5). */
export interface StageCompanion {
  turnLines: CompanionTurnLines;
  portraits: Record<string, string>;
  /** First player-turn move the 린 one-shot cheer is eligible on (default 6). */
  rinAtMove?: number;
  /** First player-turn move the 브람 one-shot cheer is eligible on (default 14). */
  bramAtMove?: number;
}

/** A one-shot ally cheer: on the player's turn at `atMove`+ it takes over the
 *  turn bar once and swaps the hero art to `portrait` for that move, then the
 *  bar returns to the opponent's voice. Independent of `companion`; used where
 *  the opponent keeps their own voiced turn bar (e.g. 토르). */
export interface StageCheer {
  atMove: number;
  name: string;
  text: Loc;
  portrait: string;
}

export interface Stage {
  id: number;
  act: Loc;
  opponentName: string;
  opponentTitle: string;
  role: "ally" | "villain";
  intro: Loc;
  winLine: Loc;
  loseLine: Loc;
  /** Result-popup heading on a win. Defaults to gt("victory"). */
  winTitle?: Loc;
  /** Result-popup reward line's outcome phrase on a win, before the disc
   *  record. Defaults to a generic "defeated {opponentName}" phrase. */
  winPhrase?: Loc;
  /** Label for the disc-playing foe in result-popup score/lose lines. Defaults
   *  to `opponentName`. Stage 3 sets "짐승" — the AI is the 균열 짐승, not 세라. */
  foeName?: string;
  voice: StageVoice;
  aiLevel: AiLevel;
  // Ally stages: "dark" (player opens). Villain stages 11–15: "light" (the
  // enemy opens — dark always moves first, so the AI does).
  playerDisc: Disc;
  reward: number; // Gamzone points on a win (test value)
  art: {
    background: string;
    /** The single opponent illustration for the match hero slot. For stage 3
     *  this is 세라 — the 균열 짐승 has no form and is never drawn. */
    opponent: string;
  };
  companion?: StageCompanion;
  cheer?: StageCheer;
  /** Normal opponent-voiced stage only: the one-shot "behind" line (first player
   *  turn the disc gap reaches ≥8) is voiced by an ally instead of the opponent —
   *  the hero art swaps to `portrait` for that one move. Replaces `voice.behind`. */
  behindAlly?: { name: string; text: Loc; portrait: string };
}

// Relative to the app base so one build works at root, subpath and subdomain.
const BG_ACT1 = "/assets/backgrounds/act-1-moss-gate-v1.webp";
const OPP_RIN = "/assets/characters/01-rin-moss-gatekeeper-rival-v1.webp";
const OPP_BRAM = "/assets/characters/02-bram-lantern-keeper-rival-v1.webp";

const L = (ko: string, en: string): Loc => ({ ko, en });

export const STAGE_1: Stage = {
  id: 1,
  act: ACT1_NAME,
  opponentName: "린",
  opponentTitle: "이끼 문지기",
  role: "ally",
  intro: L(
    "봉인의 빛에 반응한 그대가 안전한지, 린이 첫 결투로 시험한다.",
    "Rin's first duel tests whether you — the one the seal's light answered — can be trusted.",
  ),
  winLine: L(
    "“빛이 진짜였군요. 입구의 등불을 밝혀 두겠습니다. 함께 가요.”",
    "“Your light was real. I'll keep the entrance lantern lit. Let's go together.”",
  ),
  loseLine: L(
    "“아직 흔들리는 빛이에요. 숨을 고르고 다시 오세요.”",
    "“Your light still wavers. Catch your breath and come again.”",
  ),
  voice: {
    turn: L("당신 차례예요.", "Your turn."),
    slow: L("천천히 해도 괜찮아요.", "Take your time."),
    behind: L("모서리부터 노려 봐요.", "Aim for the corners first."),
    thinking: L("린이 고르는 중…", "Rin is choosing…"),
  },
  aiLevel: 1,
  playerDisc: "dark",
  reward: 50,
  art: { background: BG_ACT1, opponent: OPP_RIN },
};

export const STAGE_2: Stage = {
  id: 2,
  act: ACT1_NAME,
  opponentName: "브람",
  opponentTitle: "등불 수집가",
  role: "ally",
  intro: L(
    "브람이 등불을 들어 판을 비춘다. 가짜 빛인지 진짜 빛인지 가려낸다.",
    "Bram raises his lantern over the board. He'll tell a false light from a true one.",
  ),
  winLine: L(
    "“흔들림 없는 빛이야. 숨은 길은 내가 열어 두지.”",
    "“Not a flicker in that light. I'll open the hidden paths for you.”",
  ),
  loseLine: L(
    "“아직은 등불이 흔들려. 심지를 돋우고 다시 오게.”",
    "“Your lantern still shakes. Trim the wick and come back.”",
  ),
  voice: {
    turn: L("자, 자네 차례야.", "Now then, your turn."),
    slow: L("등불 다 닳겠어.", "That lantern's going to burn out."),
    behind: L("가장자리를 붙잡게.", "Grab hold of the edges."),
    thinking: L("브람이 살피는 중…", "Bram is studying the board…"),
  },
  aiLevel: 2,
  playerDisc: "dark",
  reward: 50,
  art: { background: BG_ACT1, opponent: OPP_BRAM },
};

/** Stage 3 — 공동 방어. Same match screen as 린·브람: the disc-playing foe is a
 *  형체 없는 균열 짐승 (`opponentName`/HUD read "짐승"), and 세라 fronts the match
 *  as the companion — the hero slot shows 세라, `art.opponent`. On the player's
 *  turn the bar is voiced by the companion (`companion.turnLines`, state-picked);
 *  on the 짐승's turn it shows `voice.thinking`. `voice.turn/slow/behind` are
 *  unused here (companion overrides them) but kept to satisfy `StageVoice`. */
export const STAGE_3: Stage = {
  id: 3,
  act: ACT1_NAME,
  opponentName: "짐승",
  // The disc-playing foe is 짐승, but the STAGE block's title line describes the
  // companion fronting the match — 세라 — and is independent of the foe.
  opponentTitle: "담쟁이 궁수",
  role: "ally",
  intro: L(
    "세라가 길목을 지키고, 주인공은 균열 짐승 한 마리와 마주선다.",
    "Sera holds the pass while the hero faces down a rift beast.",
  ),
  winLine: L("", ""),
  loseLine: L(
    "“짐승이 협곡으로 빠져나갔어. 숨을 고르고 다시 막자.”",
    "“The beast slipped through into the gorge. Catch your breath — let's hold it again.”",
  ),
  winTitle: L("균열 짐승 격퇴", "Rift Beast Repelled"),
  winPhrase: L("세라와 짐승을 물리쳤습니다", "Beat the beast alongside Sera"),
  voice: {
    turn: L("짐승의 길이 막혔어. 지금 흐름을 잡아!", "The beast's path is blocked. Take the momentum now!"),
    slow: L("조금만 더 버텨. 내가 다른 무리를 막고 있을게!", "Just hold a little longer. I've got the other pack!"),
    behind: L("밀리고 있어. 가장자리부터 되찾아!", "We're losing ground. Take the edges back!"),
    thinking: L("균열 짐승이 움직이는 중…", "The rift beast is moving…"),
  },
  aiLevel: 1,
  playerDisc: "dark",
  reward: 50,
  art: { background: BG_ACT1, opponent: SERA_PORTRAIT },
  companion: {
    turnLines: SERA_TURN_LINES,
    portraits: SERA_COMPANION_PORTRAITS,
  },
};

/** Stage 4 — 토르, 바람길 추적자 (II막 첫 대전). A straight ally trial: read
 *  the true trail. Normal opponent-voiced turn bar, no companion layer.
 *  AI level 3 = mobility squeeze (see ai.ts). */
export const STAGE_4: Stage = {
  id: 4,
  act: ACT2_NAME,
  opponentName: "토르",
  opponentTitle: "바람길 추적자",
  role: "ally",
  // [수정 2026-09-10 · 커서/마지막수 세션] 만남이 "낯선 자 신뢰 시험"으로
  // 바뀌어 intro/winLine/loseLine에서 길 읽기 프레이밍 제거. 병행 작업 시 유지.
  intro: L(
    "붉은 흔적이 갈라진 협곡, 토르가 처음 보는 주인공을 믿어도 될지 결투로 가린다.",
    "In the gorge where the red trail splits, Tor decides by duel whether this stranger can be trusted.",
  ),
  winLine: L("“믿을 만하군. 앞장서지.”", "“You're trustworthy. I'll take the lead.”"),
  loseLine: L(
    "“아직은 못 믿겠군. 숨을 고르고 다시 붙어.”",
    "“Not enough to trust yet. Catch your breath and try again.”",
  ),
  winPhrase: L("토르를 이겼습니다", "Defeated Tor"),
  voice: {
    turn: L("네 차례다.", "Your turn."),
    slow: L("흔적이 사라지겠어. 서둘러.", "The trail's going to fade. Hurry."),
    behind: L("세라가 사람 보는 눈이 낮아졌군. 가장자리를 봐.", "Sera's eye for people must be slipping. Watch the edges."),
    thinking: L("토르가 길을 읽는 중…", "Tor is reading the trail…"),
  },
  aiLevel: 3,
  playerDisc: "dark",
  reward: 50,
  art: { background: ACT2_BG, opponent: TOR_PORTRAIT },
  cheer: {
    atMove: 16,
    name: "린",
    text: L("조금 더 힘을 내. 내가 괜히 데려온 게 아니야.", "Push a little harder. I didn't bring you along for nothing."),
    portrait: RIN_PORTRAIT,
  },
};

/** Stage 5 — 균열된 구슬 공동 방어. Like 세라's stage 3: the disc-playing foe is
 *  the corrupted orb (no art), and 니아 fronts the match as the companion —
 *  `opponentName`/HUD read "균열된 구슬", the hero slot shows 니아. The turn bar
 *  is companion-voiced (니아 leads, 세라 takes "behind", 린 cheers once at move
 *  16). `voice.turn/slow/behind` are dead (companion overrides); `voice.thinking`
 *  shows on the orb's turn. AI difficulty tuning is a later detail. */
export const STAGE_5: Stage = {
  id: 5,
  act: ACT2_NAME,
  opponentName: "균열된 구슬",
  // Foe is the orb; the STAGE block's title line describes the companion
  // fronting the match — 니아 — and is independent of the foe.
  opponentTitle: "유리 구슬 점술가",
  role: "ally",
  intro: L(
    "니아의 유리 구슬이 나쁜 미래 하나만 되풀이한다. 결투로 오염을 밀어낸다.",
    "Nia's glass orb keeps repeating one bad future. The duel pushes the corruption back out.",
  ),
  winLine: L("“구슬이 다시 조용해졌어. 여러 길이 보여.”", "“The orb's gone quiet again. I can see many paths.”"),
  loseLine: L(
    "“구슬이 나쁜 미래를 다시 붙잡았어. 마음을 가다듬고 오자.”",
    "“The orb's gripped by the bad future again. Steady yourself and come back.”",
  ),
  winTitle: L("균열된 구슬 정화", "Cracked Orb Cleansed"),
  winPhrase: L("균열된 구슬을 잠재웠습니다", "Calmed the Cracked Orb"),
  voice: {
    turn: L("네 다음 수가 보여. 둬.", "I can see your next move. Play it."), // dead — companion turn bar overrides
    slow: L("미래가 흐려지고 있어. 서둘러.", "The future's clouding. Hurry."), // dead
    behind: L("아직 바꿀 수 있는 판이야. 모서리를 봐.", "The board can still be changed. Watch the corners."), // dead
    thinking: L("붉은 균열이 구슬 위로 번진다…", "Red rift-cracks spread across the orb…"),
  },
  aiLevel: 3,
  playerDisc: "dark",
  reward: 50,
  art: { background: ACT2_BG, opponent: NIA_PORTRAIT },
  companion: {
    turnLines: NIA_TURN_LINES,
    portraits: NIA_COMPANION_PORTRAITS,
    rinAtMove: 16,
  },
};

/** Stage 6 — 칼데르, 협곡의 쌍검. Ally trial: the rite duel his 명령 permits opens
 *  the gorge gate. Opponent-voiced turn bar (칼데르, 하라체), except the one-shot
 *  "behind" nudge comes from 토르 (`behindAlly`, hero art swaps to 토르 for that
 *  one move). `voice.behind` is the fallback. Draft AI values. */
export const STAGE_6: Stage = {
  id: 6,
  act: ACT2_NAME,
  opponentName: "칼데르",
  opponentTitle: "협곡의 쌍검",
  role: "ally",
  intro: L(
    "협곡 관문은 의식 결투를 거친 자에게만 열린다. 칼데르가 원정대 대표와 겨룬다.",
    "The gorge gate opens only for one who has passed the rite duel. Calder faces the expedition's representative.",
  ),
  winLine: L("“의식이 끝났다. 관문을 연다.”", "“The rite is done. I'll open the gate.”"),
  loseLine: L(
    "“관문은 아직 열리지 않는다. 호흡을 고르고 다시 서라.”",
    "“The gate does not open yet. Steady your breath and stand again.”",
  ),
  winPhrase: L("칼데르를 이겼습니다", "Defeated Calder"),
  voice: {
    turn: L("두어라.", "Make your move."),
    slow: L("망설이는 자에게 관문은 열리지 않는다.", "The gate does not open for those who hesitate."),
    behind: L("밀리면 모서리로 물러서라. 거기서 다시 세운다.", "If you're pushed back, retreat to the corners. Rebuild from there."), // fallback — see behindAlly
    thinking: L("칼데르가 쌍검을 겨눈다…", "Calder levels his twin blades…"),
  },
  aiLevel: 4, // 막 II 보스
  playerDisc: "dark",
  reward: 50,
  art: { background: ACT2_BG, opponent: CALDER_PORTRAIT },
  behindAlly: {
    name: "토르",
    text: L("밀리면 모서리로 물러서라. 거기서 다시 계획을 세운다.", "If you're pushed back, retreat to the corners. Rebuild your plan from there."),
    portrait: TOR_PORTRAIT,
  },
};

/** Stage 7 — 오르나, 비문 해독사 (막 III 개막). Not a test — the inscription's
 *  last line is a locked riddle, and the match is the two of them reading it out
 *  together. Opponent-voiced turn bar (오르나, 해체 — weary scholar, matches
 *  ORNA_MEET), no companion layer. AI level 3 placeholder (beat tendency
 *  "안정 돌·모서리 연결" is a later detail). */
export const STAGE_7: Stage = {
  id: 7,
  act: ACT3_NAME,
  opponentName: "오르나",
  opponentTitle: "비문 해독사",
  role: "ally",
  intro: L(
    "비문의 마지막 한 줄은 글자가 아니라 수수께끼로 잠겨 있다. 오르나와 판을 사이에 두고 그 줄을 함께 읽어 낸다.",
    "The inscription's last line isn't locked by letters but by a riddle. You and Orna read it together, across the board.",
  ),
  winLine: L("“마지막 줄이 읽힌다. 끝까지 버텨 줬군.”", "“The last line reads clear now. You saw it through to the end.”"),
  loseLine: L("“아직 못 읽겠어. 자리를 고르고 다시 앉자.”", "“Still can't read it. Let's settle in and sit again.”"),
  winTitle: L("비문 해독", "Inscription Deciphered"),
  winPhrase: L("비문의 마지막 줄을 읽어 냈습니다", "Read the inscription's last line"),
  voice: {
    turn: L("수수께끼를 풀 자리를 찾아.", "Find the spot that solves the riddle."),
    slow: L("서두르면 딴 뜻이 돼.", "Rush it, and it means something else."),
    behind: L("무너진 자리부터 다시 읽어.", "Reread from where it broke down."),
    thinking: L("이 줄을 읽는 중이야…", "Reading this line…"),
  },
  aiLevel: 3,
  playerDisc: "dark",
  reward: 50,
  art: { background: ACT3_BG, opponent: ORNA_PORTRAIT },
  cheer: {
    atMove: 16,
    name: "린",
    text: L("이 한 줄이 봉인을 되돌릴 열쇠예요.", "This one line is the key to restoring the seal."),
    portrait: RIN_PORTRAIT,
  },
};

/** Stage 8 — 마레크, 청동 거상. An old bronze-armoured gate-guard, an acquaintance
 *  of 브람's, who won't lend the inner door's key (the 청동 인장) but loves a
 *  wager. The hero bets it against "원하는 건 뭐든" and plays the board out. Not
 *  asleep, not "woken." Opponent-voiced turn bar (마레크, dry, plays a patient
 *  edge-hold grind). 토르 chime mid-match. AI level 3 placeholder ("가장자리·라인
 *  장악" is a later detail). 마레크 is the sole exception to the no-betting
 *  register. */
export const STAGE_8: Stage = {
  id: 8,
  act: ACT3_NAME,
  opponentName: "마레크",
  opponentTitle: "청동 거상",
  role: "ally",
  intro: L(
    "안쪽 문의 열쇠 — 청동 인장 — 은 마레크가 쥐고 있다. 곱게 빌려주진 않지만, 내기라면 얘기가 다르다. 인장을 걸고 끝까지 겨룬다.",
    "Marek holds the key to the inner door — the bronze seal. He won't just lend it, but a wager's a different story. You play it out with the seal on the line.",
  ),
  winLine: L("“내가 졌군. 내기는 내기다 — 인장은 네 것이다.”", "“I lost. A wager's a wager — the bronze seal is yours.”"),
  loseLine: L("“내기는 졌으면 끝. 다시 걸고 싶으면 돌아와라.”", "“Lose the wager, it's over. Come back if you want another go.”"),
  winTitle: L("청동 인장 획득", "Bronze Seal Won"),
  winPhrase: L("마레크의 청동 인장을 얻었습니다", "Won Marek's bronze seal"),
  voice: {
    turn: L("내기 두는 사람 어디 갔나?", "Where'd the one making the wager go?"),
    slow: L("서두르지 마라. 어차피 승리는 내 것이다.", "No need to rush. The win's mine regardless."),
    behind: L("아직 걸 만한 자리가 있다. 거기부터 다시.", "There's still ground worth staking. Start there again."), // fallback — see behindAlly
    thinking: L("가장자리부터 잡는다…", "Taking the edges first…"),
  },
  aiLevel: 4,
  playerDisc: "dark",
  reward: 50,
  art: { background: ACT3_BG, opponent: MAREK_PORTRAIT },
  // The "behind" nudge is 린, worried about the open-ended "원하는 건 뭐든" stake.
  behindAlly: {
    name: "린",
    text: L("지면 안 돼. 어떤 걸 요구할지 모른다구…", "You can't lose. There's no telling what he'll ask for…"),
    portrait: RIN_PORTRAIT,
  },
  cheer: {
    atMove: 30,
    name: "토르",
    text: L("이 내기, 끝까지만 버티면 인장은 우리 거야.", "Just hold on to the end of this wager, and the seal's ours."),
    portrait: TOR_PORTRAIT,
  },
};

/** Stage 9 — 이브라, 달그림자 무희. 위장 작전: a loud public board with 이브라
 *  holds enemy scouts' eyes while the party opens an escape route. Opponent-voiced
 *  turn bar (이브라, calm/tactical, movement & rhythm imagery — reads which way the
 *  tide has tilted). 세라 chime mid-match (scouts still watching). AI level 3
 *  placeholder ("국면별 가중치 전환" is a later detail). 이브라 stays at the 뜰
 *  (조연). */
export const STAGE_9: Stage = {
  id: 9,
  act: ACT3_NAME,
  opponentName: "이브라",
  opponentTitle: "달그림자 무희",
  role: "ally",
  intro: L(
    "달그림자 뜰, 능선엔 적 정찰병. 이브라와 시선을 끄는 사이 동료들이 퇴로를 연다.",
    "In the Moonshadow Court, enemy scouts watch from the ridge. While you and Ivra hold their eyes, the others open an escape route.",
  ),
  winLine: L("“정찰병이 등을 돌렸어. 퇴로는 열렸다.”", "“The scouts turned their backs. The route's open.”"),
  loseLine: L("“박자가 엉켰어. 숨을 고르고 다시 붙자.”", "“The rhythm's off. Catch your breath and try again.”"),
  winTitle: L("위장 작전 성공", "Diversion Successful"),
  winPhrase: L("정찰병의 눈을 따돌렸습니다", "Threw off the scouts"),
  voice: {
    turn: L("정찰병들의 시선을 끌어야 해.", "We need to hold the scouts' eyes."),
    slow: L("박자를 놓치지 마. 정찰병들이 눈치챈다.", "Don't lose the rhythm. The scouts will notice."),
    behind: L("이렇게 끝나면 정찰병들이 눈치챌 거야. 시간이 더 필요해.", "If it ends like this, the scouts will catch on. We need more time."),
    thinking: L("정찰병들의 상태를 살핀다…", "Watching the scouts…"),
  },
  aiLevel: 4,
  playerDisc: "dark",
  reward: 50,
  art: { background: ACT3_BG, opponent: IBRA_PORTRAIT },
  cheer: {
    atMove: 20,
    name: "세라",
    text: L("정찰병 아직 이쪽 봐. 조금만 더 끌어.", "The scouts are still watching this way. Hold them a little longer."),
    portrait: SERA_PORTRAIT,
  },
};

/** Stage 10 — 로웬, 가시 재판관 (막 III 완료 노드). 공개 재판: the duel is the
 *  판결 of whether the party can carry the expedition's responsibility — 로웬
 *  weighs the *path*, not just the win. Opponent-voiced turn bar (로웬, grave,
 *  formal — process over result). 세라 chime mid-match. AI level 3 placeholder
 *  ("후보 수 선별 후 탐색" is a later detail). 로웬 stays as 판관 (조연). */
export const STAGE_10: Stage = {
  id: 10,
  act: ACT3_NAME,
  opponentName: "로웬",
  opponentTitle: "가시 재판관",
  role: "ally",
  intro: L(
    "가시 궁정 문 앞. 로웬이 공개 재판을 연다 — 원정의 책임을 질 자격을, 결과가 아니라 그 길로 판결한다.",
    "At the Thorn Court gate, Rowen holds open trial — judging by the path taken, not the result, whether you're fit to bear the expedition's responsibility.",
  ),
  winLine: L("“판결한다. 원정대는 궁정을 지날 자격이 있다.”", "“My verdict: this expedition has earned the right to cross the court.”"),
  loseLine: L("“아직 원정의 무게를 혼자 지려는군. 다시 와라.”", "“You still mean to carry the expedition's weight alone. Come again.”"),
  winTitle: L("가시 궁정 통행권", "Thorn Court Passage"),
  winPhrase: L("로웬의 판결을 통과했습니다", "Passed Rowen's judgment"),
  voice: {
    turn: L("결과만이 아니라, 거기 이르는 길을 본다.", "I watch not just the result, but the path that reaches it."),
    slow: L("침묵도 답이지만, 지금은 골라야 한다.", "Silence is an answer too, but now you must choose."),
    behind: L("판결은 아직 끝나지 않았다. 네 길을 보여라.", "The verdict isn't in yet. Show me your path."),
    thinking: L("쉬운 선택이 늘 공정하진 않지…", "The easy choice isn't always the fair one…"),
  },
  aiLevel: 5, // 막 III 보스
  playerDisc: "dark",
  reward: 50,
  art: { background: ACT3_BG, opponent: LOWEN_PORTRAIT },
  cheer: {
    atMove: 18,
    name: "세라",
    text: L("이건 재판이야. 이기는 것보다 어떻게 두는지가 중요해 보여.", "This is a trial. How you play seems to matter more than winning."),
    portrait: SERA_PORTRAIT,
  },
};

/** Stage 11 — 드라벤, 붉은 창의 기사 (막 IV 개막, first VILLAIN). 벨라's 선봉장
 *  holds the 가시 궁정 entrance; beating him drops the first 방벽. Villain
 *  turn bar — 드라벤 needles rather than encourages, and `behind` twists the
 *  party's own "함께" theme against the player. **Player is 백/후공**
 *  (`playerDisc:"light"` — 드라벤 opens). AI level 3 placeholder ("강한 압박·
 *  확장 탐색" is a later detail). No `cheer`/companion. */
export const STAGE_11: Stage = {
  id: 11,
  act: ACT4_NAME,
  opponentName: "드라벤",
  opponentTitle: "붉은 창의 기사",
  role: "villain",
  intro: L(
    "궁정 입구를 봉쇄한 벨라의 선봉장. 통행권은 통하지 않는다 — 붉은 창을 꺾어야 첫 방벽이 무너진다.",
    "Vela's vanguard blocks the court entrance. The passage token means nothing here — only breaking the red lance brings down the first wall.",
  ),
  winLine: L("“내 창이… 내가 뚫리다니…”", "“My lance... and I'm the one who's broken...”"),
  loseLine: L("“너의 실력으로 나를 지날 수 없다. 다시 와도 같을 거다.”", "“Your skill won't get you past me. It'll be the same if you come back.”"),
  winTitle: L("첫 방벽 돌파", "First Wall Broken"),
  winPhrase: L("드라벤의 봉쇄를 뚫었습니다", "Broke through Draven's blockade"),
  voice: {
    turn: L("길을 내줄 생각은 없다.", "I have no intention of stepping aside."),
    slow: L("나를 통과하는 건 어림없지!", "Getting past me is out of the question!"),
    behind: L("동료의 실망하는 모습이 훤히 보이는군.", "I can already picture your friends' disappointment."),
    thinking: L("나의 창을 받아라!", "Take my lance!"),
  },
  aiLevel: 4,
  playerDisc: "light",
  reward: 50,
  art: { background: ACT4_BG, opponent: DRABEN_PORTRAIT },
  cheer: {
    atMove: 20,
    name: "토르",
    text: L("창은 한 방향으로만 찔러. 옆을 파고들어.", "A lance only thrusts one way. Cut in from the side."),
    portrait: TOR_PORTRAIT,
  },
};

/** Stage 12 — 실렌, 수정 사서 (막 IV, second VILLAIN). 벨라's obsessive archivist
 *  has locked the party's past duels into her 수정책; winning it back means
 *  rewriting its ending on the board. Villain turn bar — 실렌 needles with
 *  records/ink imagery (choices already written, predictable). **Player is 백/
 *  후공** (`playerDisc:"light"` — 실렌 opens). AI level 3 placeholder ("빠른 종반
 *  전환·깊은 탐색" is a later detail). 린 chime mid-match. */
export const STAGE_12: Stage = {
  id: 12,
  act: ACT4_NAME,
  opponentName: "실렌",
  opponentTitle: "수정 사서",
  role: "villain",
  intro: L(
    "사람들의 선택과 미래를 수정책에 고정하려는 타락한 사서. 되돌리려면 그 책의 결말을 판 위에서 다시 써야 한다.",
    "A corrupted archivist fixing people's choices and futures into her crystal book. To undo it, you must rewrite the book's ending on the board.",
  ),
  winLine: L("“글자가… 스스로 바뀌고 있어.”", "“The letters... they're changing on their own.”"),
  loseLine: L("“기록 완료. 네 이야기는 여기까지다.”", "“Record complete. Your story ends here.”"),
  winTitle: L("수정책 탈환", "Crystal Book Reclaimed"),
  winPhrase: L("실렌의 수정책을 되찾았습니다", "Reclaimed Silen's crystal book"),
  voice: {
    turn: L("이미 적힌 결말을 바꾸겠다고?", "You mean to change an ending already written?"),
    slow: L("빈칸이 길어지는군. 내가 채워 줄까?", "That blank's dragging on. Shall I fill it for you?"),
    behind: L("네 선택은 예상 범위 안이다.", "Your choices fall exactly within what I expected."),
    thinking: L("기록은 망설이지 않아. 그래서 아름답지…", "A record never hesitates. That's what makes it beautiful…"),
  },
  aiLevel: 5,
  playerDisc: "light",
  reward: 50,
  art: { background: ACT4_BG, opponent: SILEN_PORTRAIT },
  cheer: {
    atMove: 20,
    name: "린",
    text: L("결말은 아직 안 정해졌어요. 우리가 씁니다.", "The ending isn't decided yet. We're the ones writing it."),
    portrait: RIN_PORTRAIT,
  },
};

export const STAGE_13: Stage = {
  id: 13,
  act: ACT4_NAME,
  opponentName: "카이로스",
  opponentTitle: "황혼의 사냥꾼",
  role: "villain",
  intro: L(
    "심장부로 가는 사냥문을 지키는 냉혹한 효율주의자. 동료 하나를 꾀어 제 발로 투항하게 만들었다 — 그 선택을 판 위에서 무르고 데려와야 한다.",
    "A cold-blooded believer in efficiency guards the hunt gate to the heart chamber. He talked one companion into surrendering herself — you have to undo that choice on the board and bring her back.",
  ),
  winLine: L("“내가 틀릴 리 없어… 뭉치는 건 비효율적이라고!”", "“I can't have been wrong... sticking together is inefficient!”"),
  loseLine: L("“하나를 못 버렸으니, 모두가 피해를 보는 거다.”", "“Couldn't let go of one, so now everyone pays for it.”"),
  winTitle: L("황혼의 사냥문 돌파", "Dusk Hunt Gate Broken"),
  winPhrase: L("카이로스의 사냥문을 넘었습니다", "Passed Kairos's hunt gate"),
  voice: {
    turn: L("넷을 다 지키려는 쪽은 느려.", "Trying to protect all four makes you slow."),
    slow: L("결정을 못 하는군. 역시 전체를 살린다는 건 불가능해.", "Can't decide, can you. Saving everyone really is impossible."),
    behind: L("넷을 지고 가는 손은 무겁기 마련이야.", "Carrying all four is bound to weigh a hand down."), // fallback — see behindAlly
    thinking: L("사냥은 끝을 정해 놓고 시작하는 거다.", "A hunt begins with the ending already decided."),
  },
  aiLevel: 5, // 막 IV 보스
  playerDisc: "light",
  reward: 50,
  art: { background: ACT4_BG, opponent: KAIROS_PORTRAIT },
  // The "behind" nudge is 브람 — hero art swaps to 브람 for that one move.
  behindAlly: {
    name: "브람",
    text: L("힘을 내. 린을 구해야 돼.", "Hang in there. We have to save Rin."),
    portrait: BRAM_PORTRAIT,
  },
  cheer: {
    atMove: 20,
    name: "세라",
    text: L("린, 정신 차려. 넌 속고 있어!", "Rin, snap out of it. You're being deceived!"),
    portrait: SERA_PORTRAIT,
  },
};

/** Stage 14 — 아르덴, 균열에 물든 파수꾼 (막 V, 해방 duel). Mechanically a villain
 *  slot (`playerDisc:"light"`, 후공) but the fiction is
 *  a rescue, not a fight to break him — winning brings back the guardian's own
 *  will. His voice is fragmented/looping (corruption talking), not a taunt.
 *  `behindAlly` = 린 — she was just freed from her own manipulation in
 *  KAIROS_MEET/OUTRO and reads his state from the inside. `winLine` is synced
 *  to ARDEN_OUTRO's first line. */
export const STAGE_14: Stage = {
  id: 14,
  act: ACT5_NAME,
  opponentName: "아르덴",
  opponentTitle: "균열에 물든 파수꾼",
  role: "villain",
  intro: L(
    "봉인의 심장 아래, 사슬에 묶인 아르덴은 균열에 잠식되어 초점 없는 눈으로 판을 마주한다. 꺾는 게 아니라 — 그 안에 남은 수호자의 자아를 되돌려야 한다.",
    "Beneath the Seal Heart, Arden sits chained, consumed by the rift, facing the board with unfocused eyes. This isn't about breaking him — it's about bringing back the guardian still left inside.",
  ),
  winLine: L("“…시야가, 돌아온다…… 내가, 얼마나 오래……”", "“...My sight, is coming back... how long, was I......”"),
  loseLine: L("“물러서라…… 나는, 심장을, 지킨다……”", "“Get back... I, protect, the heart......”"),
  winTitle: L("아르덴 해방", "Arden Freed"),
  winPhrase: L("벨라에게 빼앗긴 아르덴의 자아를 되돌렸습니다", "Restored Arden's self, stolen by Vela"),
  voice: {
    turn: L("……침입자.", "......Intruder."),
    slow: L("…멈추지, 마라……", "...Don't, stop......"),
    behind: L("물러서라…… 나는, 지킨다……", "Get back... I, protect it......"), // fallback — see behindAlly
    thinking: L("사슬이, 울린다……", "The chains, are ringing......"),
  },
  aiLevel: 5,
  playerDisc: "light",
  reward: 50,
  art: { background: ACT5_BG, opponent: ARDEN_PORTRAIT },
  // 린이 카이로스전에서 겪은 조종을 되짚으며 건네는 격려.
  behindAlly: {
    name: "린",
    text: L("포기하지 마요. 저 안엔 아직 진짜 아르덴이 있어요.", "Don't give up. The real Arden is still in there."),
    portrait: RIN_PORTRAIT,
  },
  cheer: {
    atMove: 20,
    name: "토르",
    text: L("밀어붙여. 저 눈이 다시 흔들리고 있어.", "Push through. His eyes are wavering again."),
    portrait: TOR_PORTRAIT,
  },
};

/** Stage 15 — 벨라, 잿빛 균열의 왕비 (막 V, 캠페인 최종전). Calm and genuinely
 *  convinced, not a raving villain — her voice stays composed even as the
 *  match turns, matching BELA_OUTRO's dignified-defeat tone (no gloating
 *  loseLine either way). `behindAlly` = 세라 (protective scout, rallies the
 *  whole party rather than needling); `cheer` = 토르 at a later move (25) —
 *  the final fight earns a longer fuse than the earlier villain stages'
 *  move-20 cheers. `winLine` is synced to BELA_OUTRO's first line. */
export const STAGE_15: Stage = {
  id: 15,
  act: ACT5_NAME,
  opponentName: "벨라",
  opponentTitle: "잿빛 균열의 왕비",
  role: "villain",
  intro: L(
    "봉인의 심장을 두고 벌이는 마지막 결투. 벨라를 막아야 흩어진 빛과 봉인이 제자리로 돌아온다.",
    "The final duel over the Seal Heart. Only by stopping Vela can the scattered lights and the seal return to where they belong.",
  ),
  winLine: L("“…아, 내 질서가, 내 질서를 무시하다니……”", "“...Ah, my order... my order, defied......”"),
  loseLine: L("“질서 없는 선택이 부른 결과다. 다시 오거라.”", "“This is what choice without order brings. Come again.”"),
  winTitle: L("균열의 왕좌 저지", "Rift Throne Overthrown"),
  winPhrase: L("벨라의 폭주를 막고 봉인을 되돌렸습니다", "Stopped Vela and restored the seal"),
  voice: {
    turn: L("네 선택, 지켜보마.", "I'll watch your choice."),
    slow: L("선택을 못해 망설이는군. 내가 그 고민을 줄여주마.", "Hesitating over a choice. Let me ease that burden for you."),
    behind: L("이길 수 없는 싸움이다.", "This is a fight you cannot win."), // fallback — see behindAlly
    thinking: L("질서를 새로 쓰는 중……", "Rewriting order......"),
  },
  aiLevel: 6, // 최종 보스
  playerDisc: "light",
  reward: 50,
  art: { background: ACT5_BG, opponent: BELA_PORTRAIT },
  behindAlly: {
    name: "세라",
    text: L("포기하지 마! 다 같이 여기까지 왔잖아.", "Don't give up! We made it this far together."),
    portrait: SERA_PORTRAIT,
  },
  cheer: {
    atMove: 25,
    name: "토르",
    text: L("차분하게 봐. 여기까지 온 이유가 있잖아.", "Stay calm. There's a reason we made it this far."),
    portrait: TOR_PORTRAIT,
  },
};

export const STAGES: Stage[] = [
  STAGE_1,
  STAGE_2,
  STAGE_3,
  STAGE_4,
  STAGE_5,
  STAGE_6,
  STAGE_7,
  STAGE_8,
  STAGE_9,
  STAGE_10,
  STAGE_11,
  STAGE_12,
  STAGE_13,
  STAGE_14,
  STAGE_15,
];

export function getStage(id: number): Stage {
  const s = STAGES.find((x) => x.id === id);
  if (!s) throw new Error(`stage ${id} is not part of this test build`);
  return s;
}
